import { useEffect, useMemo, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useForm } from "react-hook-form";
import { CierreTurnoApi } from "../api/CierreTurnoApi";
import { getErrorFromResponse } from "../../../../../../../../utility/utils";
import { PESO_KG_PACA_PRODUCCION } from "../../../../../../../../utility/constants";
import {
    agruparSobrantesPorPadrePT,
    combinarTotalesPadresConSobrantes,
} from "../../../helpers/sobrantesTurno";

export const useCierreTurnoForm = ({ irATurno }) => {
    const [turnoActualId, setTurnoActualId] = useState(null);
    const [totalesProduccion, setTotalesProduccion] = useState({});
    const [totalesPadresPT, setTotalesPadresPT] = useState([]);
    const [productosTrabajados, setProductosTrabajados] = useState([]);
    const [sobrantesTurno, setSobrantesTurno] = useState([]);

    const {
        getTotalesProduccion,
        getProductosTrabajadosTurno,
        cerrarTurno,
        cargando,
    } = CierreTurnoApi();

    const {
        control: formControl,
        getValues: formGetValues,
        setValue: formSetValue,
        trigger: validateForm,
        watch: formWatch,
        formState: { errors: formErrors },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            pacas_secadora: { value: false, label: "No" },
            hay_sobrante_turno: { value: false, label: "No" },
            enviar_bodega_sobrante: false,
        },
    });

    const GetTotalesProduccion = async () => {
        try {
            const response = await getTotalesProduccion();

            setTurnoActualId(response.turno_id || null);
            setTotalesProduccion(response.totales_produccion || {});
            setTotalesPadresPT(response.totales_padres_pt || []);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener los totales de producción.",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            setTurnoActualId(null);
            setTotalesProduccion({});
            setTotalesPadresPT([]);
        }
    };

    const GetProductosTrabajados = async () => {
        const productos = await getProductosTrabajadosTurno();
        setProductosTrabajados(productos);
    };

    useEffect(() => {
        GetTotalesProduccion();
        GetProductosTrabajados();
    }, []);

    const AgregarSobrante = () => {
        const productoSobrante = formGetValues("producto_sobrante");
        const kgConforme =
            parseFloat(formGetValues("kg_secos_sobrantes_conforme_nuevo")) || 0;
        const kgNoConforme =
            parseFloat(formGetValues("kg_secos_sobrantes_no_conforme_nuevo")) ||
            0;

        if (!productoSobrante) {
            NotificationManager.error(
                "Debe seleccionar el producto del sobrante",
                "Error",
                3000,
            );
            return;
        }

        if (!kgConforme && !kgNoConforme) {
            NotificationManager.error(
                "Debe ingresar los kg secos sobrantes conforme y/o no conforme",
                "Error",
                3000,
            );
            return;
        }

        if (
            sobrantesTurno.some(
                (item) =>
                    item.orden_produccion_id ===
                    productoSobrante.orden_produccion_id,
            )
        ) {
            NotificationManager.error(
                "Ya se agregó un sobrante para este producto",
                "Error",
                3000,
            );
            return;
        }

        setSobrantesTurno([
            ...sobrantesTurno,
            {
                orden_produccion_id: productoSobrante.orden_produccion_id,
                producto_nombre:
                    productoSobrante.padre_producto_terminado_nombre
                        ? `${productoSobrante.padre_producto_terminado_nombre} - ${productoSobrante.producto_nombre}`
                        : productoSobrante.producto_nombre,
                padre_producto_terminado_id:
                    productoSobrante.padre_producto_terminado_id,
                padre_producto_terminado_nombre:
                    productoSobrante.padre_producto_terminado_nombre,
                kg_secos_sobrantes_conforme: kgConforme,
                kg_secos_sobrantes_no_conforme: kgNoConforme,
            },
        ]);

        formSetValue("producto_sobrante", null);
        formSetValue("kg_secos_sobrantes_conforme_nuevo", "");
        formSetValue("kg_secos_sobrantes_no_conforme_nuevo", "");
    };

    const EliminarSobrante = (index) => {
        setSobrantesTurno(
            sobrantesTurno.filter((_, itemIndex) => itemIndex !== index),
        );
    };

    const CerrarTurno = async () => {
        const isValid = await validateForm();
        if (!isValid) return;

        const pacasSecadora = formGetValues("pacas_secadora");
        const cantidadPacasSecadora = formGetValues("cantidad_pacas_secadora");
        const ordenPacasSecadora = formGetValues(
            "orden_produccion_pacas_secadora",
        );
        const haySobranteTurnoSeleccion = formGetValues("hay_sobrante_turno");
        const enviarBodegaSobrante = formGetValues("enviar_bodega_sobrante");

        const data = {
            cantidad_pacas_secadora: 0,
            orden_produccion_pacas_secadora_id: null,
            sobrantes: [],
            sobrante_enviado_bodega: false,
        };

        if (pacasSecadora && pacasSecadora.value) {
            data.cantidad_pacas_secadora = cantidadPacasSecadora || 0;
            data.orden_produccion_pacas_secadora_id = ordenPacasSecadora
                ? ordenPacasSecadora.orden_produccion_id
                : null;
        }

        if (haySobranteTurnoSeleccion && haySobranteTurnoSeleccion.value) {
            data.sobrantes = sobrantesTurno.map((item) => ({
                orden_produccion_id: item.orden_produccion_id,
                kg_secos_sobrantes_conforme: item.kg_secos_sobrantes_conforme,
                kg_secos_sobrantes_no_conforme:
                    item.kg_secos_sobrantes_no_conforme,
            }));
            data.sobrante_enviado_bodega = enviarBodegaSobrante || false;
        }

        try {
            await cerrarTurno(data);

            NotificationManager.success(
                "Turno cerrado exitosamente",
                "Éxito",
                3000,
            );

            irATurno();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al cerrar el turno",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    const pacasSecadora = formWatch("pacas_secadora");
    const cantidadPacasSecadora = formWatch("cantidad_pacas_secadora");
    const haySobranteTurnoSeleccion = formWatch("hay_sobrante_turno");

    const hayPacasEnSecadora = pacasSecadora && pacasSecadora.value;
    const haySobranteTurno =
        haySobranteTurnoSeleccion && haySobranteTurnoSeleccion.value;

    const pesoPacasSecadora = hayPacasEnSecadora
        ? (cantidadPacasSecadora || 0) * PESO_KG_PACA_PRODUCCION
        : 0;

    const pesoSobranteTurno = haySobranteTurno
        ? sobrantesTurno.reduce(
              (sum, item) =>
                  sum +
                  (item.kg_secos_sobrantes_conforme || 0) +
                  (item.kg_secos_sobrantes_no_conforme || 0),
              0,
          )
        : 0;

    const totalProduccionAproximada =
        (totalesProduccion.kg_pacas_paletizadas || 0) +
        pesoPacasSecadora +
        pesoSobranteTurno;

    // kg secos sobrantes reportados en el turno, agrupados por padre PT
    const sobrantesPorPadrePT = useMemo(() => {
        if (!haySobranteTurno) return {};

        return agruparSobrantesPorPadrePT(sobrantesTurno);
    }, [sobrantesTurno, haySobranteTurno]);

    // filas de la tabla de padres PT con los kg sobrantes sumados al total producido;
    // los padres PT que solo tienen sobrante se agregan con el resto de totales en cero
    const totalesPadresPTConSobrantes = useMemo(
        () =>
            combinarTotalesPadresConSobrantes(
                totalesPadresPT,
                sobrantesPorPadrePT,
            ),
        [totalesPadresPT, sobrantesPorPadrePT],
    );

    const totalesProduccionConSobrantes = {
        ...totalesProduccion,
        kg_secos_sobrantes: pesoSobranteTurno,
        kg_producidos:
            (totalesProduccion.kg_producidos || 0) + pesoSobranteTurno,
    };

    return {
        turnoActualId,
        totalesProduccion,
        totalesPadresPTConSobrantes,
        totalesProduccionConSobrantes,
        productosTrabajados,
        sobrantesTurno,
        cargando,

        formControl,
        formErrors,

        hayPacasEnSecadora,
        haySobranteTurno,
        pesoPacasSecadora,
        totalProduccionAproximada,

        AgregarSobrante,
        EliminarSobrante,
        CerrarTurno,
    };
};
